// Menu-generation strategies — how each day of a generated menu is filled.
//
// A strategy describes the shape of a day:
//
//   { name, slots: [{ tag, count }], fillTags: [tag, …], isDefault? }
//
// `slots` are the explicit meal slots to place first (e.g. 1 Breakfast, 1 Lunch,
// 1 Dinner), in order. `fillTags` is the pool of tags used to top the day up
// toward the calorie goal afterwards (e.g. Snack, Fruit) — items matching ANY
// fill tag are pooled and chosen at random. See model/menu.js generateMenuRange.
//
// SCOPE — strategies live in one of two DynamoDB partitions:
//   • PRIVATE: PK = the caller's Cognito Identity ID (their own strategies).
//   • SHARED:  PK = "shared" (visible/editable by every signed-in user), like
//     the ingredient catalogue. The built-in default lives here so everyone
//     starts with it; editing/deleting a shared strategy affects all users.
//
// Both are stored under SK `strategy#<id>`. listStrategies merges both, tagging
// each row with `scope: 'shared' | 'private'`. The shared default uses a FIXED
// id (`strategy#default`) and is lazily seeded (idempotent) on first list.

import { getItem, listItems, updateItem, deleteItem, SHARED_PARTITION_KEY } from './db.js';

/** SK prefix for strategy records. */
export const STRATEGY_PREFIX = 'strategy#';

/** Fixed id of the shared, built-in default strategy. */
export const DEFAULT_STRATEGY_ID = `${STRATEGY_PREFIX}default`;

/** The built-in default: one of each meal, snacks to fill the rest. */
export const DEFAULT_STRATEGY = Object.freeze({
  name: 'Standard (B/L/D + snacks)',
  slots: [
    { tag: 'Breakfast', count: 1 },
    { tag: 'Lunch', count: 1 },
    { tag: 'Dinner', count: 1 },
  ],
  fillTags: ['Snack'],
  // When true, an ingredient tagged with one of this strategy's tags can be
  // placed directly as a single-serve meal (a virtual recipe) for that tag.
  tagsMatchIngredients: true,
  isDefault: true,
});

/** De-duplicate a list of tags case-insensitively (first-seen casing wins). */
function dedupeTags(tags) {
  const seen = new Map();
  for (const t of tags ?? []) {
    const tag = String(t ?? '').trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (!seen.has(key)) seen.set(key, tag);
  }
  return [...seen.values()];
}

/** Normalise a strategy's stored shape (defensive against partial input). */
function normalise(strategy) {
  const stored = {
    name: String(strategy.name ?? '').trim() || 'Untitled strategy',
    slots: (strategy.slots ?? [])
      .map((s) => ({ tag: String(s.tag ?? '').trim(), count: Math.max(0, Number(s.count) || 0) }))
      .filter((s) => s.tag && s.count > 0),
    fillTags: dedupeTags(strategy.fillTags),
    // Default ON when unspecified (covers pre-existing rows saved through the
    // editor before this field existed — the consumer also treats missing as on).
    tagsMatchIngredients: strategy.tagsMatchIngredients !== false,
  };
  if (strategy.isDefault) stored.isDefault = true;
  return stored;
}

/**
 * Whether a strategy allows ingredients tagged with its tags to be used as
 * single-serve virtual meals. Treats a missing flag as ON, so strategies stored
 * before the field existed keep the previous (snack-style) behaviour.
 */
export function tagsMatchIngredients(strategy) {
  return strategy?.tagsMatchIngredients !== false;
}

/** The partition key for a given scope. */
function partitionFor(userId, scope) {
  return scope === 'shared' ? SHARED_PARTITION_KEY : userId;
}

/**
 * List all strategies visible to the user: their private strategies plus the
 * shared ones, each tagged with `scope`. Lazily seeds the shared default if it
 * is missing (idempotent — fixed id, so concurrent seeds converge).
 */
export async function listStrategies(doc, userId) {
  const [shared, mine] = await Promise.all([
    listItems(doc, SHARED_PARTITION_KEY, STRATEGY_PREFIX),
    listItems(doc, userId, STRATEGY_PREFIX),
  ]);

  let sharedRows = shared;
  if (!shared.some((s) => s.itemId === DEFAULT_STRATEGY_ID)) {
    await seedDefaultStrategy(doc);
    sharedRows = [
      ...shared,
      { userId: SHARED_PARTITION_KEY, itemId: DEFAULT_STRATEGY_ID, ...DEFAULT_STRATEGY },
    ];
  }

  return [
    ...sharedRows.map((s) => ({ ...s, scope: 'shared' })),
    ...mine.map((s) => ({ ...s, scope: 'private' })),
  ];
}

/** Write the built-in default into the shared partition (idempotent). */
export function seedDefaultStrategy(doc) {
  return updateItem(doc, SHARED_PARTITION_KEY, DEFAULT_STRATEGY_ID, { ...DEFAULT_STRATEGY });
}

/** Fetch one strategy by id within a scope, or null. */
export function getStrategy(doc, userId, itemId, scope = 'private') {
  return getItem(doc, partitionFor(userId, scope), itemId);
}

/**
 * Create a new strategy in the given scope (default private). Returns the stored
 * row (with generated itemId + scope). Use a random id for new strategies;
 * the shared default's fixed id is reserved for the seed.
 */
export async function createStrategy(doc, userId, strategy, scope = 'private') {
  const itemId = `${STRATEGY_PREFIX}${crypto.randomUUID()}`;
  const stored = normalise(strategy);
  await updateItem(doc, partitionFor(userId, scope), itemId, stored);
  return { userId: partitionFor(userId, scope), itemId, scope, ...stored };
}

/** Update an existing strategy in place (id unchanged). Returns the stored row. */
export async function updateStrategy(doc, userId, itemId, strategy, scope = 'private') {
  const stored = normalise(strategy);
  await updateItem(doc, partitionFor(userId, scope), itemId, stored);
  return { userId: partitionFor(userId, scope), itemId, scope, ...stored };
}

/** Delete a strategy by id within a scope. */
export function deleteStrategy(doc, userId, itemId, scope = 'private') {
  return deleteItem(doc, partitionFor(userId, scope), itemId);
}

/**
 * Collect the distinct tags referenced by a set of strategies (slot tags +
 * fill tags), for tag-input suggestions. Case-insensitive, first-casing wins.
 */
export function collectStrategyTags(strategies) {
  const all = [];
  for (const s of strategies ?? []) {
    for (const slot of s.slots ?? []) all.push(slot.tag);
    for (const t of s.fillTags ?? []) all.push(t);
  }
  return dedupeTags(all).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
